import React from 'react'
import * as Lodash from 'lodash'
import * as LodashFP from 'lodash/fp'

import {
  areEmptyFieldGuidelines,
  FieldGuidelinesType,
  FormBlueprint,
  Guidelines,
  ValueGuideline,
  GuidelineCondition,
  Guideline,
  FormFieldBlueprint,
} from '../../interfaces/components/dynamic-form'
import numeral from '../../utils/number-locale'

import {
  getBlueprintAtPath,
  removeUselessConditionalGuidelines,
} from '../../lib/dynamic-form/helpers'

import {
  translateWithUserLocale,
  __tryToTranslateWithUserLocale__do_not_use_unless_absolutely_needed,
} from '../../utils/i18n'
import { removeFirstElementFromPathString } from '../../utils/string'
import { Divider } from '@design/pt-ui'
import { Creative } from '../../interfaces/entities/creative'
import { IStringAnyMap } from '../../types/map'

type BorderColor = 'red' | 'green' | 'blue'

/**
 * Convert number secs to display time
 *
 * 65 input becomes 01:05.
 *
 *
 */
const toMMSS = (inputSeconds: any) => {
  const secs = parseInt(inputSeconds)
  const minutes = Math.floor(secs / 60)
  const seconds = secs - minutes * 60

  if (secs === 0) {
    return `0 ${translateWithUserLocale('units.seconds')}`
  }

  if (secs <= 60) {
    return secs + ` ${translateWithUserLocale('units.seconds')}`
  }

  return (
    minutes +
    ` ${translateWithUserLocale('units.minutes')} ` +
    seconds +
    ` ${translateWithUserLocale('units.seconds')}`
  )
}

const getAmountWithUnit = (property: string, amount: number) => {
  if (property === 'width' || property === 'height') {
    return amount + ' ' + translateWithUserLocale('units.pixels')
  }

  if (property === 'size') {
    return numeral(amount).format('0.0 b')
  }

  if (property === 'duration') {
    return toMMSS(amount)
  }

  return amount
}

const valueGuidelineString = (guideline: ValueGuideline, property: string) => {
  const { min, max, values } = guideline

  if (max && min) {
    if (max === min) {
      return translateWithUserLocale('dynamic_form.guidelines.exact', {
        amount: getAmountWithUnit(property, min),
      })
    }

    return translateWithUserLocale('dynamic_form.guidelines.between', {
      min: getAmountWithUnit(property, min),
      max: getAmountWithUnit(property, max),
    })
  }

  if (min) {
    return translateWithUserLocale('dynamic_form.guidelines.min', {
      min: getAmountWithUnit(property, min),
    })
  }

  if (max) {
    return translateWithUserLocale('dynamic_form.guidelines.max', {
      max: getAmountWithUnit(property, max),
    })
  }

  if (values) {
    return translateWithUserLocale('dynamic_form.guidelines.values', {
      value_1: getAmountWithUnit(property, values[0]),
      value_2: getAmountWithUnit(property, values[1]),
    })
  }

  return null
}

const GuidelineConditionString = (
  cond: GuidelineCondition,
  formBlueprint: FormBlueprint,
) => {
  const fieldBlueprint = getBlueprintAtPath(
    formBlueprint,
    removeFirstElementFromPathString(cond.field),
  )

  const baseTranslationContext = {
    field: fieldBlueprint ? fieldBlueprint.label : cond.field,
  }

  if (cond.type === 'empty') {
    const translationContext = baseTranslationContext
    if (cond.complement) {
      return translateWithUserLocale(
        'dynamic_form.guidelines.with',
        translationContext,
      )
    } else {
      return translateWithUserLocale(
        'dynamic_form.guidelines.without',
        translationContext,
      )
    }
  }

  if (cond.type === 'in') {
    const values = cond.values.length > 0 ? cond.values : ['🤪']
    const [value, ...rest] = values
    const translationContext = {
      ...baseTranslationContext,
      value,
    }
    if (cond.complement) {
      return (
        translateWithUserLocale(
          'dynamic_form.guidelines.when_not',
          translationContext,
        ) + rest.map((v) => ` ${translateWithUserLocale('or')} ${v}`).join(' ')
      )
    } else {
      return (
        translateWithUserLocale(
          'dynamic_form.guidelines.when',
          translationContext,
        ) + rest.map((v) => ` ${translateWithUserLocale('or')} ${v}`).join(' ')
      )
    }
  }

  return null
}

const GuidelineConditionsString = (
  cond: GuidelineCondition[],
  formBlueprint: FormBlueprint,
) => {
  return cond
    .map((cond) => GuidelineConditionString(cond, formBlueprint))
    .filter((cond) => cond !== null)
    .join(' ' + translateWithUserLocale('and') + ' ')
}

const guidelinesBoxStyles = () => ({
  alignItems: 'stretch',
  marginRight: 15,
  flexFlow: 'column',
})

const GuidelinesList = (guideline: Guideline, extra?: string[]) => {
  return (
    <ul
      style={{
        fontSize: 14,
        listStyleType: 'none',
        paddingInlineStart: 0,
      }}>
      {Lodash.map(guideline, (range, property) => {
        if (range.max || range.min || range.values) {
          return (
            <li
              style={{
                padding: 3,
              }}
              key={property}>
              {__tryToTranslateWithUserLocale__do_not_use_unless_absolutely_needed(
                'dynamic_form.guidelines.property.' + property,
                property,
              )}
              : {valueGuidelineString(range, property)}
            </li>
          )
        }
      })}

      {Lodash.map(extra, (s, i) => (
        <li
          style={{
            padding: 3,
            maxWidth: 350,
          }}
          key={`extra-${i}`}>
          <span dangerouslySetInnerHTML={{ __html: s }} />
        </li>
      ))}
    </ul>
  )
}

const GuidelinesComponent = (props: {
  guidelines: Guidelines
  formBlueprint: FormBlueprint
  borderColor?: BorderColor
  label: string
  extra?: string[]
}) => {
  if (
    props.guidelines.conditional.length === 0 &&
    Object.keys(props.guidelines.unconditional).length === 0 &&
    (props.extra || []).filter((e) => e).length === 0
  ) {
    return null
  }

  return (
    <div style={guidelinesBoxStyles()}>
      <div>
        <h4 style={{ fontSize: 18, marginBlockStart: 0 }}>{props.label}</h4>
      </div>
      <div>
        {GuidelinesList(props.guidelines.unconditional, props.extra)}

        {props.guidelines.conditional.map((conditionalGuideline, index) => {
          const durationCond = conditionalGuideline.conditions.find(
            (c) =>
              (c.type === 'between' || c.type === 'min' || c.type === 'max') &&
              c.field.includes('duration'),
          )
          const widthMin = conditionalGuideline.guidelines.width?.min

          if (durationCond && widthMin) {
            const widthStr = `${widthMin} ${translateWithUserLocale('units.pixels')}`
            let text: string | null = null

            if (durationCond.type === 'between') {
              text = translateWithUserLocale(
                'dynamic_form.guidelines.condition.between',
                {
                  min: durationCond.min,
                  max: durationCond.max,
                  width: widthStr,
                },
              )
            } else if (durationCond.type === 'min') {
              text = translateWithUserLocale(
                'dynamic_form.guidelines.condition.min',
                {
                  min: getAmountWithUnit('duration', durationCond.min),
                  width: widthStr,
                },
              )
            } else if (durationCond.type === 'max') {
              text = translateWithUserLocale(
                'dynamic_form.guidelines.condition.max',
                {
                  max: getAmountWithUnit('duration', durationCond.max),
                  width: widthStr,
                },
              )
            }

            if (text) {
              return (
                <ul
                  key={index}
                  style={{
                    fontSize: 14,
                    listStyleType: 'none',
                    paddingInlineStart: 0,
                  }}>
                  <li style={{ padding: 3 }}>{text}</li>
                </ul>
              )
            }
          }

          return (
            <React.Fragment key={index}>
              <h5>
                {GuidelineConditionsString(
                  conditionalGuideline.conditions,
                  props.formBlueprint,
                )}
              </h5>
              {GuidelinesList(conditionalGuideline.guidelines)}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
export type Props = {
  fieldBlueprint: FormFieldBlueprint
  formBlueprint: FormBlueprint
  guidelines: FieldGuidelinesType
  platform: string
  creative?: Creative
}

const FieldGuidelinesComponent = (props: Props) => {
  if (areEmptyFieldGuidelines(props.guidelines)) {
    return null
  }

  const isInstagramStoryReels = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['instagram_positions'].includes(position)) {
        if (
          Array.isArray(placements) &&
          (placements.includes('story') || placements.includes('reels'))
        ) {
          return true
        }
      }

      return false
    })
  }

  const isInstagramStory = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['instagram_positions'].includes(position)) {
        if (Array.isArray(placements) && placements.includes('story')) {
          return true
        }
      }

      return false
    })
  }

  const isInstagramReels = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['instagram_positions'].includes(position)) {
        if (Array.isArray(placements) && placements.includes('reels')) {
          return true
        }
      }

      return false
    })
  }

  const isInstagramProfileReels = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['instagram_positions'].includes(position)) {
        if (Array.isArray(placements) && placements.includes('profile_reels')) {
          return true
        }
      }

      return false
    })
  }

  const isFbAdsOnReels = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['facebook_positions'].includes(position)) {
        if (
          Array.isArray(placements) &&
          placements.includes('facebook_reels_overlay')
        ) {
          return true
        }
      }

      return false
    })
  }

  const isFbStory = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['facebook_positions'].includes(position)) {
        if (Array.isArray(placements) && placements.includes('story')) {
          return true
        }
      }

      return false
    })
  }

  const isFbFeed = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['facebook_positions'].includes(position)) {
        if (Array.isArray(placements) && placements.includes('feed')) {
          return true
        }
      }

      return false
    })
  }

  const isPAOutbrain = (allPlacements: IStringAnyMap) => {
    return Object.entries(allPlacements).some(([position, placements]) => {
      if (['performance_advertising'].includes(position)) {
        if (placements === 'performance_advertising_outbrain') {
          return true
        }
      }

      return false
    })
  }

  /**
   * Hardcoded extra guidelines for Facebook
   *
   * Recommendation engine does not support all rules available in validator.
   * So some of them need to be attached manually to Guideline.
   */
  const extraGuidelinesFacebook = {
    required: {
      video: [''],
      image: [''],
    },
    recommended: {
      video: [
        translateWithUserLocale(
          'dynamic_form.guidelines.hardcoded.video.sound',
        ),
      ],
      image: [
        translateWithUserLocale(
          'dynamic_form.guidelines.facebook.hardcoded.image.file_type',
        ),
      ],
    },
  }

  const formatsets = props.creative?.project?.formatsets
  const creativeName = props.creative?.type?.name

  if (
    formatsets &&
    formatsets[0] &&
    (isFbAdsOnReels(formatsets[0].data.placement) ||
      isInstagramProfileReels(formatsets[0].data.placement) ||
      isInstagramReels(formatsets[0].data.placement))
  ) {
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.hardcoded.video.supported_file_type',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    !isFbAdsOnReels(formatsets[0].data.placement) &&
    !isInstagramProfileReels(formatsets[0].data.placement) &&
    !isInstagramReels(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.hardcoded.video.file_type',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    creativeName === 'carousel' &&
    isInstagramStory(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.carousel.instagram_stories_ratio',
      ),
    )
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.carousel.instagram_stories_ratio',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    creativeName === 'carousel' &&
    isInstagramProfileReels(formatsets[0].data.placement) &&
    !isFbFeed(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.carousel.instagram_profile_reels_ratio',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    isInstagramReels(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.logo_consideration_instagram_reels_profile_reels',
      ),
    )
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.logo_consideration_instagram_reels_profile_reels',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    creativeName !== 'carousel' &&
    isInstagramProfileReels(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.logo_consideration_instagram_reels_profile_reels',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    isInstagramStory(formatsets[0].data.placement) &&
    !isInstagramReels(formatsets[0].data.placement)
  ) {
    if (creativeName === 'carousel') {
      extraGuidelinesFacebook.recommended.video.push(
        translateWithUserLocale(
          'dynamic_form.guidelines.facebook.hardcoded.carousel.logo_consideration_instagram_stories',
        ),
      )
      extraGuidelinesFacebook.recommended.image.push(
        translateWithUserLocale(
          'dynamic_form.guidelines.facebook.hardcoded.carousel.logo_consideration_instagram_stories',
        ),
      )
    } else {
      extraGuidelinesFacebook.recommended.video.push(
        translateWithUserLocale(
          'dynamic_form.guidelines.facebook.hardcoded.video.logo_consideration_instagram',
        ),
      )
      extraGuidelinesFacebook.recommended.image.push(
        translateWithUserLocale(
          'dynamic_form.guidelines.facebook.hardcoded.image.logo_consideration_instagram',
        ),
      )
    }
  }

  if (
    formatsets &&
    formatsets[0] &&
    isFbStory(formatsets[0].data.placement) &&
    !isInstagramStoryReels(formatsets[0].data.placement)
  ) {
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.logo_consideration',
      ),
    )
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.logo_consideration_image',
      ),
    )
  }

  if (
    formatsets &&
    formatsets[0] &&
    creativeName !== 'carousel' &&
    isFbFeed(formatsets[0].data.placement) &&
    !isInstagramStoryReels(formatsets[0].data.placement) &&
    creativeName !== 'collection_catalog'
  ) {
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.feed_recommended_height',
      ),
    )
    extraGuidelinesFacebook.required.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.feed_required_height',
      ),
    )
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.hardcoded.image.logo_facebook_feeds',
      ),
    )
  }

  if (creativeName === 'carousel') {
    extraGuidelinesFacebook.recommended.image.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.carousel.advantage+_catalog_ads',
      ),
    )
    extraGuidelinesFacebook.recommended.video.push(
      translateWithUserLocale(
        'dynamic_form.guidelines.facebook.carousel.advantage+_catalog_ads',
      ),
    )
  }

  /**
   * Hardcoded extra guidelines for PIA Advertising
   */
  const extraGuidelinesPerformanceAdvertising = {
    required: {
      video: [
        translateWithUserLocale(
          'dynamic_form.guidelines.performance_advertising.hardcoded.video.file_type',
        ),
        translateWithUserLocale(
          'dynamic_form.guidelines.performance_advertising.hardcoded.video.ssl',
        ),
      ],
      image: [
        translateWithUserLocale(
          'dynamic_form.guidelines.performance_advertising.hardcoded.image.file_type',
        ),
        translateWithUserLocale(
          'dynamic_form.guidelines.performance_advertising.hardcoded.image.recommendation',
        ),
      ],
    },
    recommended: {
      video: [''],
      image: [''],
    },
  }

  if (
    formatsets &&
    formatsets[0] &&
    isPAOutbrain(formatsets[0].data.placement)
  ) {
    extraGuidelinesPerformanceAdvertising.required.image = [
      translateWithUserLocale(
        'dynamic_form.guidelines.performance_advertising.hardcoded.image.file_type_outbrain',
      ),
      translateWithUserLocale(
        'dynamic_form.guidelines.performance_advertising.hardcoded.image.recommendation',
      ),
    ]
  }

  const extraGuidelines = {
    facebook: extraGuidelinesFacebook,
    performance_advertising: extraGuidelinesPerformanceAdvertising,
  }
  const platform = props.platform as 'facebook' | 'performance_advertising'
  const fileType: string | null = LodashFP.getOr(
    null,
    ['meta', 'fileType'],
    props.fieldBlueprint,
  )

  let extra = (rec: 'recommended' | 'required') => {
    if (fileType === 'video' || fileType === 'image') {
      return extraGuidelines[platform][rec][fileType]
    } else {
      // Filtering empty guidelines
      const imageGuidelines =
        extraGuidelines[platform][rec].image.filter(Boolean)
      const videoGuidelines =
        extraGuidelines[platform][rec].video.filter(Boolean)
      const imageArray =
        imageGuidelines.length > 0
          ? [
              `${translateWithUserLocale(
                'dynamic_form.guidelines.hardcoded.if_image',
              )}:`,
              ...imageGuidelines.map((l) => `&nbsp; &nbsp; ${l}`),
            ]
          : []
      const videoArray =
        videoGuidelines.length > 0
          ? [
              `${translateWithUserLocale(
                'dynamic_form.guidelines.hardcoded.if_video',
              )}:`,
              ...videoGuidelines.map((l) => `&nbsp; &nbsp; ${l}`),
            ]
          : []
      return [...imageArray, ...videoArray]
    }
  }

  return (
    <div
      className="dynamic-form-field-guidelines"
      style={{
        height: '100%',
        display: 'flex',
        flexFlow: 'column',
        flex: 0.5,
        padding: 15,
      }}>
      <GuidelinesComponent
        guidelines={removeUselessConditionalGuidelines(
          props.guidelines.requirements,
          props.formBlueprint,
        )}
        formBlueprint={props.formBlueprint}
        extra={extra('required')}
        label={translateWithUserLocale('dynamic_form.guidelines.requirements')}
      />
      <Divider />
      <GuidelinesComponent
        guidelines={removeUselessConditionalGuidelines(
          props.guidelines.recommendations,
          props.formBlueprint,
        )}
        formBlueprint={props.formBlueprint}
        extra={extra('recommended')}
        label={translateWithUserLocale(
          'dynamic_form.guidelines.recommendations',
        )}
      />
    </div>
  )
}

export default FieldGuidelinesComponent
